/**
 * Reusable custom styles for react-select components
 * Supports light/dark mode using CSS custom properties
 * * Usage:
 * import { customSelectStyles } from 'util/selectStyles';
 * <Select styles={customSelectStyles} ... />
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { StylesConfig } from "react-select";

export interface CommonListingFilterOption {
    value: string;
    label: string;
}

export const customSelectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        // Background remains exactly the same in disabled and enabled modes
        backgroundColor: 'var(--select-control-bg)',
        borderRadius: '8px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: state.isDisabled ? '#cbd5e1' : 'var(--rs-border, #d1d5db)',
        padding: '2px',
        boxShadow: 'none',
        cursor: state.isDisabled ? 'not-allowed' : 'default',
        // Forces react-select not to fade out the component
        opacity: 1, 
        '&:hover': {
            borderColor: state.isDisabled ? '#cbd5e1' : 'var(--color-primary)',
        },
    }),
    singleValue: (base: any, state: any) => ({
        ...base,
        // Text becomes gray in disabled state if a default value is present
        color: state.isDisabled ? '#9ca3af' : 'var(--select-text)',
        opacity: 1,
        fontSize: 14, // Added to match text-sm
    }),
    input: (base: any) => ({
        ...base,
        color: 'var(--select-text)',
        fontSize: 14, // Added to match text-sm
    }),
    placeholder: (base: any, state: any) => ({
        ...base,
        color: state.isDisabled ? '#9ca3af' : 'var(--select-placeholder)',
        fontSize: 14, // Added to match text-sm
    }),
    menu: (base: any) => ({
        ...base,
        backgroundColor: 'var(--select-menu-bg)',
        border: '1px solid var(--select-border)',
        borderRadius: '8px',
        overflow: 'hidden',
        zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
    menuList: (base: any) => ({
        ...base,
        backgroundColor: 'transparent',
    }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--select-option-selected)'
            : state.isFocused
                ? 'var(--select-option-hover)'
                : 'transparent',
        color: state.isSelected ? '#ffffff' : 'var(--select-text)',
        cursor: 'pointer',
        fontSize: 14, // Added to match text-sm
        '&:active': {
            backgroundColor: 'var(--select-option-active)',
        },
    }),
};

export const commonListingFilterSelectStyles: StylesConfig<CommonListingFilterOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        height: 38,
        borderRadius: 8,
        // FIX: Use theme border variable for both states
        borderColor: "var(--rs-border, #d1d5db)",
        boxShadow: "none",
        backgroundColor: "var(--select-control-bg)",
        paddingInline: 0,
        cursor: state.isDisabled ? "not-allowed" : "default",
        // FIX: Fade the entire control to visually indicate disabled state
        opacity: state.isDisabled ? 0.5 : 1, 
        "&:hover": {
            borderColor: state.isDisabled ? "var(--rs-border, #d1d5db)" : "var(--color-primary)",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        paddingInline: 5,
    }),
    singleValue: (base) => ({
        ...base,
        // FIX: Rely on control opacity instead of hardcoded gray
        color: "var(--select-text)", 
        fontSize: 14,
        opacity: 1,
    }),
    input: (base) => ({
        ...base,
        color: "var(--select-text)",
        fontSize: 14,
    }),
    placeholder: (base) => ({
        ...base,
        color: "var(--select-placeholder)",
        fontSize: 14,
        fontWeight: 400,
    }),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: "var(--select-border)",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: "var(--select-placeholder)",
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: "var(--select-menu-bg)",
        border: "1px solid var(--select-border)",
        borderRadius: 12,
        overflow: "hidden",
        zIndex: 9999,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        backgroundColor: "transparent",
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? "var(--select-option-selected)"
            : state.isFocused
                ? "var(--select-option-hover)"
                : "transparent",
        color: state.isSelected ? "#ffffff" : "var(--select-text)",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        fontSize: 14,
        "&:active": {
            backgroundColor: "var(--select-option-active)",
        },
    }),
};